// src/components/admin/UserList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import { AdminShell } from './Sidebar';
import {
  StatCard, RefreshButton, FilterPill, SearchField,
  TableCard, LoadingState, EmptyState, Pagination,
} from '../ui/CrmPage';

const ROLES = { 1: 'Администратор', 2: 'Оператор', 3: 'Пользователь' };
const ROLE_OPTIONS = [
  { id: 'all', label: 'Все роли' },
  { id: '1',   label: 'Администраторы' },
  { id: '2',   label: 'Операторы' },
  { id: '3',   label: 'Пользователи' },
];

const formatPhone = (raw) => {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    const p = digits.split('');
    return `+7 ${p.slice(1, 4).join('')} ${p.slice(4, 7).join('')} ${p.slice(7, 9).join('')} ${p.slice(9).join('')}`;
  }
  return raw;
};
const fullName = (u) =>
  [u?.profile_lastname, u?.profile_firstname, u?.profile_secondname].filter(Boolean).join(' ') || '—';
const initials = (name, fallback = 'U') =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || fallback;

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

export default function UserList() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('login');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${apiBase}v1/users_profile`, { signal: ctrl.signal });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`.trim());
        }
        const data = await res.json();
        if (!Array.isArray(data)) throw new Error('Ожидается массив');
        setRows(data);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, reloadKey]);

  const totals = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((u) => u.is_active).length;
    const disabled = total - active;
    const clients = rows.filter((u) => u.profile_is_client).length;
    return { total, active, disabled, clients };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((u) => {
      if (roleFilter !== 'all' && String(u.id_role) !== roleFilter) return false;
      if (statusFilter !== 'all') {
        const active = !!u.is_active;
        if (statusFilter === 'active' && !active) return false;
        if (statusFilter === 'disabled' && active) return false;
      }
      if (!term) return true;
      const fio = fullName(u).toLowerCase();
      const email = (u.profile_email || '').toLowerCase();
      return (
        (u.login || '').toLowerCase().includes(term) ||
        fio.includes(term) ||
        email.includes(term) ||
        (u.phone_number || '').toLowerCase().includes(term)
      );
    });
  }, [rows, q, roleFilter, statusFilter]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'full_name':         return (u) => fullName(u).toLowerCase();
      case 'login':             return (u) => (u.login || '').toLowerCase();
      case 'profile_email':     return (u) => (u.profile_email || '').toLowerCase();
      case 'id_role':           return (u) => String(u.id_role || '');
      case 'profile_is_client': return (u) => (u.profile_is_client ? 1 : 0);
      case 'phone_number':      return (u) => (u.phone_number || '');
      case 'is_active':         return (u) => (u.is_active ? 1 : 0);
      default:                  return (u) => (u[orderBy] ?? '');
    }
  }, [orderBy]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);

  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const hasFilters = q || roleFilter !== 'all' || statusFilter !== 'all';
  const resetFilters = () => { setQ(''); setRoleFilter('all'); setStatusFilter('all'); setPage(1); };

  return (
    <AdminShell
      breadcrumbs="Управление"
      title="Пользователи"
      subtitle="Учётные записи сотрудников и клиентов"
      actions={
        <button
          type="button"
          className="adminbtn adminbtn--primary"
          onClick={() => navigate('/CreateUserPage')}
        >
          <PersonAddIcon sx={{ fontSize: 18 }} />
          Создать
        </button>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats">
            <StatCard label="Всего"        value={totals.total}    accent="neutral" />
            <StatCard label="Активные"     value={totals.active}   accent="success" />
            <StatCard label="Отключённые"  value={totals.disabled} accent="neutral" />
            <StatCard label="Клиенты"      value={totals.clients}  accent="brand" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: логин, ФИО, e-mail, телефон…"
            />
            <div className="crmpage__pills">
              {ROLE_OPTIONS.map((o) => (
                <FilterPill
                  key={o.id}
                  active={roleFilter === o.id}
                  onClick={() => { setRoleFilter(o.id); setPage(1); }}
                >
                  {o.label}
                </FilterPill>
              ))}
              <FilterPill
                active={statusFilter === 'active'}
                onClick={() => { setStatusFilter(statusFilter === 'active' ? 'all' : 'active'); setPage(1); }}
              >
                Только активные
              </FilterPill>
              <FilterPill
                active={statusFilter === 'disabled'}
                onClick={() => { setStatusFilter(statusFilter === 'disabled' ? 'all' : 'disabled'); setPage(1); }}
              >
                Отключённые
              </FilterPill>
            </div>
            {hasFilters && (
              <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
            )}
          </section>

          {error && (
            <div className="crmpage__alert crmpage__alert--error">
              Не удалось загрузить пользователей. {error}
            </div>
          )}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем пользователей…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Ничего не найдено"
                text={hasFilters ? 'Попробуйте сбросить фильтры или изменить поисковый запрос.' : 'Здесь появятся пользователи.'}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 140 }} />
                      <col />
                      <col style={{ width: 160 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 130 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('login')} style={{ cursor: 'pointer' }}>
                          Логин {sortIcon(orderBy === 'login', order)}
                        </th>
                        <th onClick={() => requestSort('full_name')} style={{ cursor: 'pointer' }}>
                          ФИО {sortIcon(orderBy === 'full_name', order)}
                        </th>
                        <th onClick={() => requestSort('id_role')} style={{ cursor: 'pointer' }}>
                          Роль {sortIcon(orderBy === 'id_role', order)}
                        </th>
                        <th onClick={() => requestSort('profile_is_client')} style={{ cursor: 'pointer' }}>
                          Тип {sortIcon(orderBy === 'profile_is_client', order)}
                        </th>
                        <th onClick={() => requestSort('phone_number')} style={{ cursor: 'pointer' }}>
                          Телефон {sortIcon(orderBy === 'phone_number', order)}
                        </th>
                        <th onClick={() => requestSort('is_active')} style={{ cursor: 'pointer' }}>
                          Статус {sortIcon(orderBy === 'is_active', order)}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((u) => {
                        const name = fullName(u);
                        return (
                          <tr
                            key={u.id ?? u.login}
                            className="crmpage__row"
                            onClick={() => navigate(`/UserProfile/${u.id}`)}
                          >
                            <td><span className="crmpage__num">{u.login || '—'}</span></td>
                            <td>
                              <div className="crmpage__user-cell">
                                <span className="crmpage__avatar" aria-hidden>
                                  {initials(name, u.login?.[0]?.toUpperCase() || 'U')}
                                </span>
                                <div>
                                  <div className="crmpage__primary">{name}</div>
                                  <div className="crmpage__sub">{u.profile_email || '—'}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`crmpage__chip ${String(u.id_role) === '1' ? 'crmpage__chip--warning' : ''}`}>
                                {ROLES[String(u.id_role)] || '—'}
                              </span>
                            </td>
                            <td className="crmpage__muted">{u.profile_is_client ? 'Клиент' : 'Сотрудник'}</td>
                            <td className="crmpage__date">{formatPhone(u.phone_number)}</td>
                            <td>
                              <span className={`crmpage__status crmpage__status--${u.is_active ? 'success' : 'neutral'}`}>
                                {u.is_active ? 'Активен' : 'Отключён'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>
        </div>
      </div>
    </AdminShell>
  );
}
